<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Purchase Orders</h5>
        <p class="page-sub">Manage and track purchase orders issued to vendors</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="openExportModal('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="openExportModal('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button class="btn-primary" @click="$router.push('/procurement/purchase-orders/create')">
          <font-awesome-icon icon="plus" class="me-1" /> New Purchase Order
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search by PO number or title..." />
      <select v-model="filterStatus" @change="fetchPOs" class="filter-select">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="issued">Issued</option>
        <option value="partially_received">Partially Received</option>
        <option value="completed">Completed</option>
        <option value="pending_approval">Pending Approval</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="perPage" @change="page = 1; fetchPOs()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div><p>Loading...</p>
      </div>
      <div v-else-if="!pos.length" class="empty-state">
        <font-awesome-icon icon="file-invoice" class="empty-icon" />
        <p>No purchase orders found</p>
        <span>Create a purchase order from an approved requisition</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>PO Number</th>
            <th>Title</th>
            <th>Vendor</th>
            <th>PR Reference</th>
            <th class="th-amount">Total</th>
            <th>Expected Delivery</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="po in pos" :key="po.id">
            <td class="mono">{{ po.po_number }}</td>
            <td>{{ po.title }}</td>
            <td>{{ po.vendor?.name || '—' }}</td>
            <td class="mono">{{ po.pr?.pr_number || '—' }}</td>
            <td class="amount-col">{{ formatMoney(po.total, po.currency) }}</td>
            <td>{{ po.expected_delivery_date ? formatDate(po.expected_delivery_date) : '—' }}</td>
            <td><span :class="['status-badge', `status-badge--${po.status}`]">{{ formatStatus(po.status) }}</span></td>
            <td>
              <button class="btn-link" @click="$router.push(`/procurement/purchase-orders/${po.id}`)">View</button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pagination-bar" v-if="meta && meta.last_page > 1">
        <span class="pagination-info">Showing {{ meta.from }}–{{ meta.to }} of {{ meta.total }}</span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(1)">«</button>
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">‹ Prev</button>
          <span class="pg-pages">
            <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === meta.current_page }" @click="changePage(p)">{{ p }}</button>
          </span>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">Next ›</button>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.last_page)">»</button>
        </div>
      </div>
    </div>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost-cp" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-cp" :disabled="selectedExportCols.length === 0 || exporting" @click="doExport()">
              <font-awesome-icon :icon="exporting ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exporting" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import debounce from 'lodash/debounce';
  import jsPDF from 'jspdf';
  import 'jspdf-autotable';

  export default {
    setup() { return { toast: useToast() }; },
    data() {
      return {
        pos: [], meta: null, loading: false,
        search: '', filterStatus: '', page: 1, perPage: 5,
        dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
        showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [],
        exportColumnDefs: [
          { key: 'po_number',    label: 'PO Number',    pdfWidth: 90  },
          { key: 'title',        label: 'Title',         pdfWidth: 150 },
          { key: 'vendor',       label: 'Vendor',        pdfWidth: 130 },
          { key: 'pr_ref',       label: 'PR Reference',  pdfWidth: 90  },
          { key: 'total',        label: 'Total',         pdfWidth: 100 },
          { key: 'delivery',     label: 'Exp. Delivery', pdfWidth: 100 },
          { key: 'status',       label: 'Status',        pdfWidth: 101 },
        ],
      };
    },
    computed: {
      pageRange() {
        if (!this.meta || !this.meta.last_page) return [];
        const cur = this.meta.current_page, last = this.meta.last_page;
        const range = [];
        for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
        return range;
      },
    },
    created() {
      this.debouncedFetch = debounce(this.fetchPOs, 350);
      this.fetchPOs();
      document.addEventListener('click', this.onOutsideClick);
    },
    beforeUnmount() {
      document.removeEventListener('click', this.onOutsideClick);
    },
    methods: {
      async fetchPOs() {
        this.loading = true;
        try {
          const params = { page: this.page, per_page: this.perPage };
          if (this.search)       params.search = this.search;
          if (this.filterStatus) params.status = this.filterStatus;
          if (this.dateFrom)     params.from   = this.dateFrom;
          if (this.dateTo)       params.to     = this.dateTo;
          const { data } = await axios.get('/procurement/purchase-orders', { params });
          this.pos  = data.data?.data ?? data.data ?? [];
          this.meta = data.data?.meta ?? (data.data?.current_page ? data.data : null);
        } catch { this.toast.error('Failed to load purchase orders'); }
        finally { this.loading = false; }
      },
      changePage(p) { this.page = p; this.fetchPOs(); },
      applyDateFilter() { this.page = 1; this.fetchPOs(); },
      clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchPOs(); },
      openExportModal(format) {
        this.pendingExportFmt   = format;
        this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
        this.showExportModal    = true;
        this.exportMenuOpen     = false;
      },

      getPOValue(po, key) {
        switch (key) {
          case 'po_number': return po.po_number || '—';
          case 'title':     return po.title || '—';
          case 'vendor':    return po.vendor?.name || '—';
          case 'pr_ref':    return po.pr?.pr_number || '—';
          case 'total':     return this.formatMoney(po.total, po.currency);
          case 'delivery':  return po.expected_delivery_date ? this.formatDate(po.expected_delivery_date) : '—';
          case 'status':    return this.formatStatus(po.status);
          default:          return '—';
        }
      },

      async doExport() {
        if (!this.selectedExportCols.length) return;
        this.exporting       = true;
        this.showExportModal = false;
        try {
          const params = { page: 1, per_page: 10000 };
          if (this.search)       params.search = this.search;
          if (this.filterStatus) params.status = this.filterStatus;
          if (this.dateFrom)     params.from   = this.dateFrom;
          if (this.dateTo)       params.to     = this.dateTo;
          const { data } = await axios.get('/procurement/purchase-orders', { params });
          const rows = data.data?.data ?? data.data ?? [];
          const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
          const headers = cols.map(c => c.label);
          const body    = rows.map(r => cols.map(c => this.getPOValue(r, c.key)));
          const date    = new Date().toISOString().slice(0, 10);
          if (this.pendingExportFmt === 'csv') {
            const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = `purchase-orders-${date}.csv`; a.click();
            URL.revokeObjectURL(url);
          } else {
            const doc      = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const usable   = 761;
            const totalW   = cols.reduce((s, c) => s + c.pdfWidth, 0);
            const scale    = usable / totalW;
            const colStyles = {};
            cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
            doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
            doc.text('Purchase Orders', 40, 46);
            doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
            doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
            doc.autoTable({
              startY: 78, head: [headers], body,
              styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
              headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
              alternateRowStyles: { fillColor: [248, 250, 252] },
              columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
              didDrawPage(d) {
                doc.setFontSize(8); doc.setTextColor(192, 202, 214);
                doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
              },
            });
            doc.save(`purchase-orders-${date}.pdf`);
          }
        } catch { this.toast?.error('Export failed. Please try again.'); }
        finally  { this.exporting = false; }
      },
      onOutsideClick(e) {
        if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
      },
      formatStatus(s) { return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); },
      formatDate(d)   { return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); },
      formatMoney(v, cur) {
        const n = parseFloat(v);
        const amount = isNaN(n) ? 0 : n;
        const [int, dec] = amount.toFixed(2).split('.');
        const formatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec;
        return cur ? `${cur} ${formatted}` : formatted;
      },
    },
  };
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:20px; gap:12px; flex-wrap:wrap; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13.5px; font-weight:600; padding:10px 20px; border-radius:8px; border:none; cursor:pointer; white-space:nowrap; }
.btn-primary:hover { background:#003b8e; }
.filter-bar { display:flex; gap:10px; margin-bottom:16px; flex-wrap:wrap; }
.filter-input  { flex:1; min-width:220px; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; }
.filter-select { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; }
.card { background:#fff; border-radius:12px; border:1.5px solid #e8edf5; overflow:hidden; }
.loading-state,.empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px 24px; color:#7a90b0; gap:8px; }
.empty-icon  { font-size:32px; margin-bottom:4px; }
.spinner     { width:32px; height:32px; border:3px solid #e8edf5; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table th { background:#f4f7fb; font-weight:600; color:#5a6a85; padding:11px 14px; text-align:left; white-space:nowrap; }
.data-table td { padding:11px 14px; border-top:1px solid #f0f4fa; color:#1a2b45; vertical-align:middle; }
.data-table tbody tr:hover { background:#f9fbff; }
.mono { font-family:monospace; font-size:12.5px; }
.amount-col { font-variant-numeric:tabular-nums; text-align:right; }
.th-amount  { text-align:right !important; }
.status-badge--pending_approval { background:#fef3c7; color:#92400e; }
.btn-link    { background:none; border:none; color:#004AAD; font-size:13px; font-weight:600; cursor:pointer; padding:2px 4px; }
.btn-link:hover { text-decoration:underline; }
.status-badge { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:2px 9px; text-transform:capitalize; }
.status-badge--draft              { background:#f1f5ff; color:#004AAD; }
.status-badge--issued             { background:#fef9c3; color:#854d0e; }
.status-badge--partially_received { background:#fef3c7; color:#92400e; }
.status-badge--completed          { background:#dcfce7; color:#166534; }
.status-badge--cancelled          { background:#fee2e2; color:#991b1b; }
.pagination-bar      { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.pagination-info     { font-size:13px; color:#7a90b0; }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { padding:6px 11px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; }
.pg-btn:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; gap:3px; }
.pg-num  { min-width:34px; padding:6px 8px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; text-align:center; }
.pg-num:hover { background:#f4f7fb; border-color:#c8d6e8; }
.pg-num--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:600; }
.per-page-wrap  { display:flex; align-items:center; gap:6px; }
.per-page-label { font-size:13px; color:#5a6a85; white-space:nowrap; }
.per-page-select { padding:7px 10px; border:1.5px solid #dde5f0; border-radius:7px; font-size:13px; color:#1a2b45; background:#fff; cursor:pointer; outline:none; }
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-clear:hover{background:#e8edf5}

/* -- Column picker modal ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.btn-ghost-cp   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:1px solid #e4eaf2; cursor:pointer; font-family:inherit; }
.btn-ghost-cp:hover { background:#e4eaf2; }
.btn-primary-cp { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-cp:hover:not(:disabled) { background:#003b8e; }
.btn-primary-cp:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }
</style>
